import React from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Collapse,
  Divider,
  InputBase,
  Button,
} from "@mui/material";
import {
  ExpandLess, ExpandMore, Logout,
  LightMode, DarkMode, Search, Code,
} from "@mui/icons-material";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import sidebarItems from "./sidebarItems";
import { useAuth } from "../contexts/AuthContext";
import { isAdminUser } from "../utils/ticketManager";
import Logo from "../assets/zenopay-logo.svg";
import { useThemeMode } from "../contexts/ThemeContext";
import { Switch } from "@mui/material";

const drawerWidth = 260;

const SideBar = ({ mobileOpen, handleDrawerToggle }) => {
  const theme = useTheme();
  const { toggleTheme, mode } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [openSections, setOpenSections] = React.useState({});

  const isDark = mode === 'dark';
  const isAdmin = isAdminUser();
  const visibleSidebarItems = React.useMemo(
    () =>
      sidebarItems
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => !item.adminOnly || isAdmin),
        }))
        .filter((section) => section.items.length > 0),
    [isAdmin]
  );

  const bg        = isDark ? '#111111' : theme.palette.background.paper;
  const activeBg  = isDark ? 'rgba(11,164,128,0.13)' : '#F0FDF4';
  const activeClr = '#0BA480';
  const mutedClr  = isDark ? '#64748B' : '#94A3B8';
  const textClr   = isDark ? '#CBD5E1' : theme.palette.text.secondary;

  React.useEffect(() => {
    visibleSidebarItems.forEach((section, idx) => {
      const hasActive = section.items.some(item => item.to === location.pathname);
      if (hasActive) setOpenSections(prev => ({ ...prev, [idx]: true }));
    });
  }, [location.pathname, visibleSidebarItems]);

  const handleSectionToggle = (index) => {
    setOpenSections(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const renderNavItem = (item, isNested = false) => {
    const isActive = location.pathname === item.to;
    const Icon = item.icon;

    return (
      <ListItemButton
        key={item.to}
        component={NavLink}
        to={item.to}
        onClick={isMobile ? handleDrawerToggle : undefined}
        sx={{
          pl: isNested ? 4.5 : 2,
          pr: 1.5,
          py: 1,
          mx: 1,
          mb: 0.25,
          borderRadius: "10px",
          minHeight: 40,
          backgroundColor: isActive ? activeBg : "transparent",
          transition: "all 0.18s ease",
          "&:hover": {
            backgroundColor: isActive ? activeBg : (isDark ? 'rgba(255,255,255,0.05)' : theme.palette.action.hover),
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 32, color: isActive ? activeClr : mutedClr }}>
          <Icon sx={{ fontSize: 18 }} />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography sx={{ fontWeight: isActive ? 600 : 400, fontSize: "14px", color: isActive ? activeClr : textClr }}>
              {item.label}
            </Typography>
          }
        />
      </ListItemButton>
    );
  };

  const renderSection = (section, idx) => {
    const hasMultipleItems = section.items.length > 1;
    const isOpen = openSections[idx];
    const hasActiveChild = section.items.some(item => item.to === location.pathname);
    const firstItem = section.items[0];
    const ParentIcon = firstItem.icon;

    if (!hasMultipleItems) {
      return (
        <Box key={section.title} sx={{ mb: 0.25 }}>
          {renderNavItem(section.items[0])}
        </Box>
      );
    }

    return (
      <Box key={section.title} sx={{ mb: 0.25 }}>
        {/* Section label */}
        <Typography sx={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 1.1,
          color: mutedClr,
          px: 3,
          pt: 1.75,
          pb: 0.5,
        }}>
          {section.title}
        </Typography>

        <ListItemButton
          onClick={() => handleSectionToggle(idx)}
          sx={{
            pl: 2, pr: 1.5, py: 1, mx: 1, mb: 0.25,
            borderRadius: "10px", minHeight: 40,
            "&:hover": { backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : theme.palette.action.hover },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: hasActiveChild ? activeClr : mutedClr }}>
            <ParentIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography sx={{ fontWeight: hasActiveChild ? 600 : 400, fontSize: "14px", color: hasActiveChild ? activeClr : textClr }}>
                {section.title}
              </Typography>
            }
          />
          {isOpen
            ? <ExpandLess sx={{ fontSize: 17, color: mutedClr }} />
            : <ExpandMore sx={{ fontSize: 17, color: mutedClr }} />}
        </ListItemButton>

        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            {section.items.map(item => renderNavItem(item, true))}
          </List>
        </Collapse>
      </Box>
    );
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: bg, transition: 'background-color 0.25s ease' }}>

      {/* Logo */}
      <Box sx={{ display: "flex", alignItems: "center", height: 66, px: 2.5, mb: 0.5 }}>
        <Box
          component="img"
          src={Logo}
          alt="ZenoPay"
          sx={{
            height: 22, width: "auto", cursor: "pointer", userSelect: "none",
            transition: "opacity 0.2s ease",
            filter: isDark ? 'brightness(0) invert(1)' : 'none',
            "&:hover": { opacity: 0.8 },
          }}
          onClick={() => (window.location.href = "/dashboard")}
        />
      </Box>

      {/* Search bar */}
      <Box sx={{
        mx: 1.5, mb: 2,
        display: 'flex', alignItems: 'center', gap: 0.75,
        bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#F1F5F9',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
        borderRadius: '10px', px: 1.25, py: 0.75,
      }}>
        <Search sx={{ fontSize: 15, color: mutedClr, flexShrink: 0 }} />
        <InputBase
          placeholder="Search Anything..."
          sx={{ fontSize: 13, color: isDark ? '#94A3B8' : '#64748B', flex: 1 }}
        />
      </Box>

      {/* MENU label */}
      <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.1, color: mutedClr, px: 3, pb: 0.5 }}>
        Menu
      </Typography>

      {/* Nav items */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", "&::-webkit-scrollbar": { width: "4px" }, "&::-webkit-scrollbar-thumb": { background: isDark ? 'rgba(255,255,255,0.1)' : theme.palette.divider, borderRadius: "3px" } }}>
        <List sx={{ px: 0, py: 0 }}>
          {visibleSidebarItems.map(renderSection)}
        </List>
      </Box>

      {/* Developer API Banner */}
      <Box sx={{
        mx: 1.5, mb: 1.5,
        borderRadius: '12px',
        bgcolor: isDark ? 'rgba(11,164,128,0.08)' : '#F0FDF4',
        border: `1px solid ${isDark ? 'rgba(11,164,128,0.18)' : '#BBF7D0'}`,
        p: 2,
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#0BA480', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Code sx={{ fontSize: 15, color: '#fff' }} />
          </Box>
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: isDark ? '#F1F5F9' : '#0F172A', mb: 0.4 }}>
          Developer API
        </Typography>
        <Typography sx={{ fontSize: 11, color: mutedClr, mb: 1.25, lineHeight: 1.5 }}>
          Integrate ZenoPay payments into your application.
        </Typography>
        <Button
          size="small"
          fullWidth
          onClick={() => navigate('/new-api-key')}
          sx={{
            textTransform: 'none', fontWeight: 700, fontSize: 12,
            bgcolor: '#0BA480', color: '#fff', borderRadius: '8px',
            '&:hover': { bgcolor: '#059669' },
          }}
        >
          Get API Key
        </Button>
      </Box>

      <Divider sx={{ mx: 2, borderColor: isDark ? 'rgba(255,255,255,0.07)' : undefined }} />

      {/* Theme toggle */}
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center" gap={0.75}>
          {isDark
            ? <DarkMode sx={{ fontSize: 16, color: mutedClr }} />
            : <LightMode sx={{ fontSize: 16, color: mutedClr }} />}
          <Typography sx={{ fontSize: 13, color: textClr, fontWeight: 500 }}>
            {isDark ? 'Dark mode' : 'Light mode'}
          </Typography>
        </Box>
        <Switch
          checked={isDark}
          onChange={toggleTheme}
          size="small"
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#0BA480' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0BA480' },
          }}
        />
      </Box>

      {/* Logout */}
      <Box sx={{ px: 1, pb: 2 }}>
        <ListItemButton
          onClick={logout}
          sx={{
            pl: 2, pr: 1.5, py: 1, borderRadius: "10px",
            color: textClr, transition: "all 0.18s ease",
            "&:hover": { bgcolor: isDark ? 'rgba(185,28,28,0.1)' : "#FEF2F2", color: "#B91C1C" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
            <Logout sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText primary={<Typography sx={{ fontWeight: 500, fontSize: "14px" }}>Logout</Typography>} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={mobileOpen}
      onClose={handleDrawerToggle}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          backgroundColor: bg,
          borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : theme.palette.divider}`,
          transition: "background-color 0.25s ease",
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default SideBar;
